import { OverlayAlertService, OverlayAlertRef } from '@gridscale/ingrid/components/overlay-alert';
import { Actions, ofType } from '@ngrx/effects';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { take, debounceTime, filter } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';

import * as ContractSelectors from './../../../../../@store/account/account.selectors';
import * as ContractActions from './../../../../..//@store/account/account.actions';
import * as UserSelectors from './../../../../..//@store/user/user.selectors';
import { getAuth } from './../../../../..//@store/auth/auth.selectors';
import { CommonModule } from '@angular/common';

import { DataTableModule, Column, TableStyle } from '@gridscale/ingrid/components/data-table';
import { AccountProjectRelation } from '../../../../../@shared/models/accounts_response.model';
import { BatchService } from '../../../../../@shared/services/batch.service';
import { ToastService } from '../../../../../@shared/services/toast.service';

@Component({
    imports: [
        CommonModule,
        DataTableModule,
        TranslateModule
    ],
    selector: 'app-account-assign',
    templateUrl: './account-assign.component.html',
    styleUrls: ['./account-assign.component.scss']
})
export class AccountAssignComponent implements OnInit, OnDestroy {
  columns: Column[] = [
    {
      id: 'name',
      headline: this.translate.instant('account'),
      dataSrc: 'name',
      preselected: true,
      sortable: false,
      width: 300
    },
    {
      id: 'owern',
      headline: this.translate.instant('owner'),
      dataSrc: 'owner_email',
      preselected: true,
      sortable: false,
      width: 300
    }
  ];
  tableStyle: TableStyle = TableStyle.MODAL;

  accounts$ = this.store.select(ContractSelectors.getAvailableAccounts);
  meta$ = this.store.select(ContractSelectors.getAccountsMeta);
  loading$ = this.store.select(ContractSelectors.isAccountListLoading);
  isLoadingNext$ = this.store.select(ContractSelectors.isAccountListLoadingNext);


  selection: Partial<{
    name: string;
    object_uuid: string;
    total_row_count: number;
    relations: {
      projects?: AccountProjectRelation[] | undefined;
    };
  }>[] = [];

  private readonly loadSearchResult$ = new Subject<any>();
  private searchSub?: Subscription;
  searchTerm?: string;

  constructor(
    private overlayAlert: OverlayAlertService,
    public ref: OverlayAlertRef,
    private toastService: ToastService,
    private translate: TranslateService,
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly batchService: BatchService
  ) { }

  checker() {
    if (this.selection.length > 0) {
      this.ref.updateButton(1, { disabled: false })
    } else {
      this.ref.updateButton(1, { disabled: true })
    }
  }

  no(force = false) {
    if (!force && this.selection && this.selection.length) {
      this.overlayAlert.confirm(
        this.translate.instant('DISCARD_CHANGES_HEADLINE'),
        this.translate.instant('DISCARD_CHANGES_INTRO'),
        [
          {
            label: this.translate.instant('cancel'),
            mode: 'secondary',
            keyShortcut: 'Escape',
            callback: (button, alert) => {
              alert.close();
              alert.callback.emit(false);
            }
          },
          {
            label: this.translate.instant('DISCARD_CHANGES_BTN'),
            mode: 'danger',
            callback: (button, alert) => {
              alert.close();
              alert.callback.emit(true);
            }
          }
        ],
        'icon-a059_warning')
        .subscribe(_confirm => {
          if (_confirm) {
            this.ref.close();
          } else {
            this.ref.setFocus();
          }
        });

    } else {
      this.ref.close();
    }

    this.ref.setLoading(false);
  }

  yes() {
    this.ref.setLoading(true);

    const batch = this.batchService.init();

    this.store
      .select(getAuth)
      .pipe(
        filter(auth => auth !== undefined),
        take(1)
      )
      .subscribe(auth => {
        this.store
          .select(UserSelectors.getSelectedUser)
          .pipe(
            filter(user => user !== undefined),
            take(1)
          )
          .subscribe(user => {
            this.selection.forEach(contract => {
              batch.addRequest('partners_contracts_users_post', {
                user_uuid: user!.object_uuid,
                contract_uuid: contract.object_uuid,
                user_role: 'write',
                partner_uuid: auth?.partner_uuid
              });
            });
          });
      });

    batch.exec();
  }

  ngOnInit() {
    this.actions$.pipe(ofType(ContractActions.patchAccountUserSuccess), take(1)).subscribe(() => {
      if (this.selection.length > 0) {
        this.toastService.add({ severity: 'success', summary: this.translate.instant('account_related_success_multi') });
      } else {
        this.toastService.add({ severity: 'success', summary: this.translate.instant('account_related_success') });
      }

      this.no(true);
    });

    this.searchSub = this.loadSearchResult$.pipe(debounceTime(250)).subscribe(searchStr => {
      this.store
        .select(ContractSelectors.getAccountListConfig)
        .pipe(take(1))
        .subscribe(oldConfig => {
          const config: any = { ...oldConfig };

          if (!searchStr) searchStr = '';
          this.searchTerm = searchStr;

          config.filter = ['name~~*%%' + searchStr + '%%'];

          this.store.dispatch(
            ContractActions.setListConfig({
              config
            })
          );

          this.store.dispatch(ContractActions.loadAccounts({}));
        });
    });

    this.store.dispatch(ContractActions.setListConfig({ config: { limit: 25 } }));
    this.store.dispatch(ContractActions.loadAccounts({}));
  }

  onFilter(searchStr: string) {
    this.loadSearchResult$.next(searchStr);
  }

  onLoadNext() {
    this.store.dispatch(ContractActions.loadAccountsNext());
  }
  
  ngOnDestroy() {
    if (this.searchSub) {
      this.searchSub.unsubscribe();
    }
  }
}
